import React, { useState, useEffect, useCallback } from 'react';
import './ExportInbox.css';

const EXPORT_CHIPS = [
  'Quote in AED (Dubai/GCC)',
  'Quote in USD',
  'Offer Free Swatch Pack (Aramex)',
  'Include Bemberg & Modal Specs',
  'Air Transit 3-4 Days',
  'Make it Shorter & Direct',
  'Mention 300 DPI Artwork & NDA',
];

const DOMESTIC_CHIPS = [
  'Quote in ₹ (INR + 5% GST)',
  'Surat Factory Dispatch (3-5 days)',
  'Offer Free Swatches (Domestic Courier)',
  'Low Sampling MOQ (3m–5m)',
  'Bulk Roll Discount (50m+)',
  'Surat Transport Bilty Available',
  'Make it Shorter & Direct',
];

export default function ExportInbox({ apiBase, getToken, onAddToPipeline, activeMarket = 'all', focusThreadId = null }) {
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(focusThreadId);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL', 'NEEDS_REPLY', 'WAITING', 'UNREAD'
  const [marketFilter, setMarketFilter] = useState(activeMarket); // 'all', 'domestic', 'export'

  // AI Draft States
  const [draftBody, setDraftBody] = useState('');
  const [draftIntent, setDraftIntent] = useState('');
  const [draftSummary, setDraftSummary] = useState('');
  const [instruction, setInstruction] = useState('');
  const [isDrafting, setIsDrafting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState('auto'); // 'auto', 'wholesale', 'export'
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Sync marketFilter when parent activeMarket changes
  useEffect(() => {
    if (activeMarket) {
      setMarketFilter(activeMarket);
    }
  }, [activeMarket]);

  // Helper API call with Firebase Auth token
  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    const token = await getToken();
    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      throw new Error(data.message || `Request failed (${res.status})`);
    }
    return data;
  }, [apiBase, getToken]);

  // Load thread list
  const loadThreads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/v1/b2b-export/threads?market=${marketFilter}`);
      const list = data.threads || [];
      setThreads(list);
      if (!list.some((t) => (t.id || t.threadId) === selectedThreadId)) {
        setSelectedThreadId(list[0]?.id || list[0]?.threadId || null);
        if (!list.length) setSelectedThread(null);
      }
    } catch (err) {
      showToast(`Could not load B2B inbox: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [apiCall, marketFilter, selectedThreadId]);

  // Initial load and reload when marketFilter changes
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // Generate / Regenerate AI Draft
  const generateAiDraft = useCallback(async (threadId, customInstruction) => {
    try {
      setIsDrafting(true);
      const res = await apiCall(`/api/v1/b2b-export/threads/${threadId}/ai-draft`, 'POST', {
        instruction: customInstruction,
        market: marketFilter === 'all' ? null : marketFilter
      });
      if (res.draft) {
        setDraftBody(res.draft.replyBody || '');
        setDraftIntent(res.draft.intent || '');
        setDraftSummary(res.draft.summary || '');
        showToast('✨ AI Proposal Draft generated!');
      }
    } catch (err) {
      showToast(`Draft generation failed: ${err.message}`, 'error');
    } finally {
      setIsDrafting(false);
    }
  }, [apiCall, marketFilter]);

  // Load selected thread detail
  const loadThreadDetail = useCallback(async (id) => {
    if (!id) return;
    setSelectedThread(null);
    setDraftBody('');
    setDraftIntent('');
    setDraftSummary('');
    try {
      const data = await apiCall(`/api/v1/b2b-export/threads/${id}`);
      const t = data.thread;
      setSelectedThread(t);
      if (t.aiDraft) {
        setDraftBody(t.aiDraft.replyBody || '');
        setDraftIntent(t.aiDraft.intent || '');
        setDraftSummary(t.aiDraft.summary || '');
      } else if (t.status === 'needs_reply') {
        // If no draft exists, generate one automatically for the conversation
        generateAiDraft(id, '');
      }
    } catch (err) {
      showToast(`Could not load thread: ${err.message}`, 'error');
    }
  }, [apiCall, generateAiDraft]);

  useEffect(() => {
    if (selectedThreadId) {
      loadThreadDetail(selectedThreadId);
    }
  }, [selectedThreadId, loadThreadDetail]);

  // Sync threads from Gmail
  const handleSyncGmail = async () => {
    try {
      setSyncing(true);
      const res = await apiCall('/api/v1/b2b-export/threads/sync', 'POST');
      showToast(`Mailbox synced with export & wholesale desks (${res.count || 0} threads)`);
      await loadThreads();
    } catch (err) {
      showToast(`Sync failed: ${err.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Toggle Redraft Chip
  const toggleChip = (chipText) => {
    setInstruction((prev) => {
      const parts = prev.split(/;\s*/).map((p) => p.trim()).filter(Boolean);
      return (parts.includes(chipText) ? parts.filter((p) => p !== chipText) : [...parts, chipText]).join('; ');
    });
  };

  // Send Reply Email
  const handleSendReply = async () => {
    if (!draftBody.trim()) {
      showToast('Reply body cannot be empty', 'error');
      return;
    }
    if (!selectedThread) return;

    const threadIsDomestic = selectedThread.market === 'domestic';
    const computedAlias =
      selectedAlias === 'wholesale' ? 'wholesale' : selectedAlias === 'export' ? 'export' : threadIsDomestic ? 'wholesale' : 'export';

    try {
      setIsSending(true);
      const sendResult = await apiCall(`/api/v1/b2b-export/threads/${selectedThread.id || selectedThread.threadId}/reply`, 'POST', {
        toEmail: selectedThread.buyerEmail,
        subject: selectedThread.subject,
        replyBody: draftBody.trim(),
        fromAlias: computedAlias,
        market: threadIsDomestic ? 'domestic' : 'export'
      });
      showToast(sendResult.recordWarning || `Official email sent to ${selectedThread.buyerEmail}`, sendResult.recordWarning ? 'error' : 'success');
      // Reload thread to show new message in conversation
      await loadThreadDetail(selectedThread.id || selectedThread.threadId);
      await loadThreads();
      setDraftBody('');
      setDraftIntent('');
      setDraftSummary('');
      setInstruction('');
    } catch (err) {
      showToast(`Failed to send email: ${err.message}`, 'error');
    } finally {
      setIsSending(false);
    }
  };

  // Filter threads
  const filteredThreads = threads.filter((t) => {
    if (statusFilter === 'NEEDS_REPLY' && t.status !== 'needs_reply') return false;
    if (statusFilter === 'WAITING' && t.status !== 'waiting_on_buyer') return false;
    if (statusFilter === 'UNREAD' && !t.unread) return false;

    if (marketFilter === 'domestic' && t.market !== 'domestic') return false;
    if (marketFilter === 'export' && t.market !== 'export') return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (t.buyerName || '').toLowerCase().includes(q);
      const matchEmail = (t.buyerEmail || '').toLowerCase().includes(q);
      const matchSubject = (t.subject || '').toLowerCase().includes(q);
      const matchCompany = (t.buyerCompany || '').toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchSubject && !matchCompany) return false;
    }
    return true;
  });

  const isCurrentThreadDomestic =
    selectedThread?.market === 'domestic';

  const activeChips = isCurrentThreadDomestic ? DOMESTIC_CHIPS : EXPORT_CHIPS;

  return (
    <div className="export-inbox-container">
      {/* Toast Alert */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
          <div className={`export-toast-alert export-toast-${toast.type}`}>
            {toast.type === 'success' ? '✅' : '⚠️'} {toast.message}
          </div>
        </div>
      )}

      {/* LEFT SIDEBAR: Threads List */}
      <aside className="export-inbox-sidebar">
        <div className="export-inbox-sidebar-header">
          <div className="export-inbox-sidebar-title-row">
            <h3>
              📬 B2B Inbox
              <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                ({filteredThreads.length})
              </span>
            </h3>
            <button
              type="button"
              className="export-sync-btn"
              onClick={handleSyncGmail}
              disabled={syncing}
              title="Sync with export@ and wholesale@fabvoguestudio.com"
            >
              {syncing ? '⏳ Syncing…' : '🔄 Sync Mailbox'}
            </button>
          </div>

          {/* Market Quick Selector inside Inbox */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button
              type="button"
              className={`export-filter-chip ${marketFilter === 'all' ? 'active' : ''}`}
              onClick={() => setMarketFilter('all')}
            >
              All Inquiries
            </button>
            <button
              type="button"
              className={`export-filter-chip ${marketFilter === 'domestic' ? 'active' : ''}`}
              onClick={() => setMarketFilter('domestic')}
            >
              🇮🇳 Domestic (India)
            </button>
            <button
              type="button"
              className={`export-filter-chip ${marketFilter === 'export' ? 'active' : ''}`}
              onClick={() => setMarketFilter('export')}
            >
              🌍 Export
            </button>
          </div>

          <input
            type="text"
            className="export-search-input"
            placeholder="Search buyers, brands, subjects…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="export-filter-chips">
            <button
              type="button"
              className={`export-filter-chip ${statusFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ALL')}
            >
              All ({threads.length})
            </button>
            <button
              type="button"
              className={`export-filter-chip ${statusFilter === 'NEEDS_REPLY' ? 'active' : ''}`}
              onClick={() => setStatusFilter('NEEDS_REPLY')}
            >
              Needs Reply
            </button>
            <button
              type="button"
              className={`export-filter-chip ${statusFilter === 'WAITING' ? 'active' : ''}`}
              onClick={() => setStatusFilter('WAITING')}
            >
              Waiting
            </button>
            <button
              type="button"
              className={`export-filter-chip ${statusFilter === 'UNREAD' ? 'active' : ''}`}
              onClick={() => setStatusFilter('UNREAD')}
            >
              Unread
            </button>
          </div>
        </div>

        <ul className="export-thread-list">
          {loading && (
            <li style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              Loading inquiries…
            </li>
          )}

          {!loading && filteredThreads.length === 0 && (
            <li style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              No inquiries found matching filter.
            </li>
          )}

          {filteredThreads.map((thread) => {
            const isSelected = (thread.id || thread.threadId) === selectedThreadId;
            const isDomestic = thread.market === 'domestic';
            return (
              <li
                key={thread.id || thread.threadId}
                className={`export-thread-item ${isSelected ? 'active' : ''} ${thread.unread ? 'unread' : ''}`}
                onClick={() => setSelectedThreadId(thread.id || thread.threadId)}
              >
                <div className="export-thread-top">
                  <span className="export-thread-sender">
                    {thread.unread && <span className="export-unread-dot" />}
                    <span>{isDomestic ? '🇮🇳' : '🌍'}</span>
                    {thread.buyerName || thread.buyerEmail}
                  </span>
                  <span className="export-thread-time">
                    {thread.lastMessageDate
                      ? new Date(thread.lastMessageDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })
                      : ''}
                  </span>
                </div>

                <div className="export-thread-subject">{thread.subject}</div>
                <div className="export-thread-snippet">
                  {thread.lastMessageSnippet || 'No snippet preview'}
                </div>

                <div className="export-thread-badges">
                  <span
                    className="export-intent-tag"
                    style={{
                      background: isDomestic ? '#fef3c7' : '#eff6ff',
                      color: isDomestic ? '#b45309' : '#1d4ed8',
                      borderColor: isDomestic ? '#fde68a' : '#dbeafe'
                    }}
                  >
                    {isDomestic ? '🇮🇳 India Wholesale' : '🌍 Global Export'}
                  </span>
                  <span
                    className={`export-status-tag ${
                      thread.status === 'needs_reply' ? 'needs_reply' : 'waiting_on_buyer'
                    }`}
                  >
                    {thread.status === 'needs_reply' ? '⚡ Needs Reply' : '⏳ Waiting'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* RIGHT PANEL: Conversation & AI Suggestion */}
      <main className="export-inbox-detail">
        {!selectedThread && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
            Select an inquiry from the list to view conversation.
          </div>
        )}

        {selectedThread && (
          <>
            {/* Header */}
            <div className="export-inbox-detail-header">
              <div className="export-buyer-meta">
                <h2>
                  <span>{isCurrentThreadDomestic ? '🇮🇳' : '🌍'} {selectedThread.buyerName || 'Buyer'}</span>
                  {selectedThread.buyerCompany && (
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>
                      &bull; {selectedThread.buyerCompany}
                    </span>
                  )}
                </h2>
                <p>
                  <strong>Email:</strong> {selectedThread.buyerEmail} &bull; <strong>Subject:</strong> {selectedThread.subject}
                </p>
              </div>

              <div className="export-detail-actions">
                <button
                  type="button"
                  className="export-link-lead-btn"
                  onClick={async () => {
                    const isDom = selectedThread.market === 'domestic';
                    const saved = await onAddToPipeline?.({
                      brandName: selectedThread.buyerCompany || selectedThread.buyerName || 'New Brand',
                      contactPerson: selectedThread.buyerName || '',
                      email: selectedThread.buyerEmail || '',
                      market: isDom ? 'domestic' : 'export',
                      country: isDom ? 'India' : selectedThread.buyerEmail.endsWith('.ae') ? 'UAE' : '',
                      city: '',
                      notes: `Imported from B2B thread: ${selectedThread.subject}`,
                      linkedThreadId: selectedThread.id || selectedThread.threadId,
                      stage: 'contacted'
                    });
                    showToast(saved ? 'Buyer linked to shared pipeline' : 'Could not save buyer', saved ? 'success' : 'error');
                  }}
                  title="Create or link CRM lead in Pipeline tab"
                >
                  ➕ Add to Buyer Pipeline
                </button>
              </div>
            </div>

            {/* Conversation Messages Timeline */}
            <div className="export-messages-scroll">
              {(selectedThread.messages || []).map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`export-msg-card ${
                    msg.isOutbound ? 'export-msg-outbound' : 'export-msg-inbound'
                  }`}
                >
                  <div className="export-msg-header">
                    <span className="export-msg-sender">
                      {msg.isOutbound
                        ? (isCurrentThreadDomestic ? 'FAB VOGUE Studio® Wholesale Desk' : 'FAB VOGUE Studio® Export Desk')
                        : msg.from}
                    </span>
                    <span style={{ fontSize: 11 }}>
                      {msg.date ? new Date(msg.date).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                    </span>
                  </div>
                  <div className="export-msg-body">{msg.text}</div>
                </div>
              ))}
            </div>

            {/* AI Suggestion & Draft Panel */}
            <div className="export-ai-draft-panel">
              <div className="export-ai-header">
                <div className="export-ai-title">
                  <span className="export-ai-sparkle">✨ AI Suggestion</span>
                  <span>{draftIntent || (isCurrentThreadDomestic ? 'Domestic Wholesale Proposal' : 'Export Proposal')}</span>
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {isCurrentThreadDomestic ? '🇮🇳 ₹/m + 5% GST • 3–5 Days Surat Dispatch' : '🌍 AED/USD • 3–4 Days Air Cargo'}
                </div>
              </div>

              {draftSummary && (
                <div className="export-ai-summary-bar">
                  💡 <strong>Buyer Request:</strong> {draftSummary}
                </div>
              )}

              {/* Quick Refine Prompt Chips */}
              <div className="export-redraft-chips-container">
                <span className="export-chips-label">Quick Refine:</span>
                {activeChips.map((chip) => {
                  const isSelected = instruction.includes(chip);
                  return (
                    <button
                      key={chip}
                      type="button"
                      className={`export-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleChip(chip)}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>

              {/* Custom Instruction Input & Redraft Button */}
              <div className="export-instruction-row">
                <input
                  type="text"
                  className="export-instruction-input"
                  placeholder={
                    isCurrentThreadDomestic
                      ? 'Custom instruction (e.g. quote in ₹ with 5% GST, mention 3m sampling, offer domestic swatch pack)...'
                      : 'Custom instruction (e.g. quote in AED, mention 50m MOQ, add swatch pack offer)...'
                  }
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      generateAiDraft(selectedThread.id || selectedThread.threadId, instruction);
                    }
                  }}
                />
                <button
                  type="button"
                  className="export-redraft-btn"
                  onClick={() => generateAiDraft(selectedThread.id || selectedThread.threadId, instruction)}
                  disabled={isDrafting}
                >
                  {isDrafting ? '⏳ Regenerating…' : '🔄 Regenerate with AI'}
                </button>
              </div>

              {/* Draft Textarea */}
              <textarea
                className="export-draft-textarea"
                rows={5}
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                placeholder="AI drafted response will appear here. You can edit any line before sending..."
              />

              {/* Action Bar */}
              <div className="export-draft-actions-bar">
                <div className="export-from-alias-indicator">
                  <span>Sending from:</span>
                  <select
                    value={selectedAlias}
                    onChange={(e) => setSelectedAlias(e.target.value)}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 4,
                      border: '1px solid #cbd5e1',
                      background: '#ffffff'
                    }}
                  >
                    <option value="auto">
                      Auto ({isCurrentThreadDomestic ? 'wholesale@fabvoguestudio.com' : 'export@fabvoguestudio.com'})
                    </option>
                    <option value="wholesale">Domestic Wholesale Desk (wholesale@fabvoguestudio.com)</option>
                    <option value="export">International Export Desk (export@fabvoguestudio.com)</option>
                  </select>
                </div>

                <button
                  type="button"
                  className="export-send-approve-btn"
                  onClick={handleSendReply}
                  disabled={isSending || !draftBody.trim()}
                >
                  {isSending ? 'Sending…' : '🚀 Approve & Send Official Email'}
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
